import React, { useId, useSyncExternalStore } from 'react';

/** One hump in user units (active underline). */
const HUMP = 20;
/** Wider humps on hover only; must divide `PERIOD` so the scroll loop stays seamless. */
const HUMP_HOVER = 30;
/** One full repeat of the underline (viewBox width + SMIL translate distance). */
const PERIOD = 120;

const VB_X = 0;
const VB_Y = -0.5;
const VB_W = PERIOD;
const VB_H = 11;
const VIEWBOX = `${VB_X} ${VB_Y} ${VB_W} ${VB_H}`;

// Same geometry as always: Q control + T reflections every 20 units.
const WAVE_ACTIVE_D = `M0,5 Q10,0 ${HUMP},5${Array.from(
  { length: PERIOD / HUMP - 1 },
  (_, i) => ` T${HUMP * (i + 2)},5`,
).join('')}`;

function subscribeReducedMotion(onChange) {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function getReducedMotionSnapshot() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getReducedMotionServerSnapshot() {
  return false;
}

/**
 * Quadratic wave (`Q` at mid, peak at y=0), tiled from `fromX` to `toX`.
 * `humpWidth` = wavelength along x (active uses {@link HUMP}, hover uses {@link HUMP_HOVER}).
 */
function buildTiledWavePath(fromX, toX, humpWidth) {
  let d = `M${fromX},5 Q${fromX + humpWidth / 2},0 ${fromX + humpWidth},5`;
  for (let x = fromX + humpWidth * 2; x <= toX; x += humpWidth) {
    d += ` T${x},5`;
  }
  return d;
}

function SquigglePath({ d = WAVE_ACTIVE_D }) {
  return (
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      vectorEffect="nonScalingStroke"
    />
  );
}

/**
 * Pink squiggle under the active route (static).
 */
export function SquiggleActive() {
  return (
    <span className="site-nav__squiggle site-nav__squiggle--active" aria-hidden>
      <svg
        className="site-nav__squiggle-svg"
        viewBox={VIEWBOX}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <SquigglePath />
      </svg>
    </span>
  );
}

/**
 * Yellow squiggle on hover: wider humps than active, same scroll period. Motion =
 * seamless horizontal phase shift (SVG SMIL) so peaks/troughs travel like surf.
 */
export function SquiggleHover() {
  const id = useId().replace(/:/g, '');
  const clipId = `site-nav-squiggle-wave-clip-${id}`;

  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );

  const extendedPath = buildTiledWavePath(-PERIOD * 2, PERIOD * 3, HUMP_HOVER);

  return (
    <span className="site-nav__squiggle site-nav__squiggle--hover" aria-hidden>
      <svg
        className="site-nav__squiggle-svg site-nav__squiggle-svg--ocean"
        viewBox={VIEWBOX}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {!reducedMotion && (
          <defs>
            <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
              <rect x="0" y="-3" width={PERIOD} height="16" />
            </clipPath>
          </defs>
        )}
        {reducedMotion ? (
          <SquigglePath />
        ) : (
          <g clipPath={`url(#${clipId})`}>
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                additive="replace"
                calcMode="linear"
                from="0 0"
                to={`${-PERIOD} 0`}
                dur="750ms"
                repeatCount="indefinite"
              />
              <SquigglePath d={extendedPath} />
            </g>
          </g>
        )}
      </svg>
    </span>
  );
}
